import type {
  NarrativeOcgFinding,
  NarrativeOcgReplacement,
  NarrativeOcgRuleHit,
  NarrativeOcgSeverity,
  NarrativeOcgValidationHint,
  TimesheetValidationResponse,
  TimesheetValidationResult,
} from '../types'

const severityRank: Record<NarrativeOcgSeverity, number> = {
  error: 3,
  warning: 2,
  info: 1,
}

const collectFindings = (
  result?: TimesheetValidationResult
): NarrativeOcgFinding[] => {
  if (!result) return []
  return result.findings ?? []
}

const getHighestSeverity = (
  result?: TimesheetValidationResult
): NarrativeOcgSeverity | undefined => {
  if (!result) return undefined
  if (
    result.has_error ||
    (result.finding_counts?.error ??
      result.narrative_issue_counts?.error ??
      0) > 0
  ) {
    return 'error'
  }
  if (
    result.has_warning ||
    (result.finding_counts?.warning ??
      result.narrative_issue_counts?.warning ??
      0) > 0
  ) {
    return 'warning'
  }

  const findings = collectFindings(result)
  const severities = [
    ...findings.map(finding => finding.severity),
    ...(result.narrative_rule_hits ?? []).map(hit => hit.severity),
  ].filter(Boolean) as NarrativeOcgSeverity[]

  return severities.sort((a, b) => severityRank[b] - severityRank[a])[0]
}

const collectReplacements = (
  result?: TimesheetValidationResult
): NarrativeOcgReplacement[] => {
  if (!result) return []

  const findings = collectFindings(result)
  const fromFindings = findings.flatMap(
    finding =>
      finding.replacements ?? finding.suggested_adjustment?.replacements ?? []
  )
  const fromHits = (result.narrative_rule_hits ?? []).flatMap(
    hit => hit.replacements ?? []
  )

  return [...fromFindings, ...fromHits]
}

const collectRuleHits = (
  result?: TimesheetValidationResult
): NarrativeOcgRuleHit[] => {
  if (!result) return []
  return result.narrative_rule_hits ?? []
}

const firstDefined = (
  ...values: Array<string | undefined>
): string | undefined =>
  values.find(value => typeof value === 'string' && value.trim().length > 0)

const getPrimaryKeyword = (
  result?: TimesheetValidationResult,
  replacements: NarrativeOcgReplacement[] = []
): string | undefined => {
  const findings = collectFindings(result)
  return firstDefined(
    replacements[0]?.term,
    findings[0]?.matched_terms?.[0],
    result?.narrative_rule_hits?.[0]?.matched_terms?.[0]
  )
}

const getPrimaryReason = (
  result?: TimesheetValidationResult,
  replacements: NarrativeOcgReplacement[] = []
): string | undefined => {
  const findings = collectFindings(result)
  return firstDefined(
    replacements[0]?.reason,
    replacements[0]?.suggestion,
    replacements[0]?.source_text,
    findings[0]?.message,
    result?.narrative_rule_hits?.[0]?.message
  )
}

export function normalizeTimesheetValidationResponse(
  response: TimesheetValidationResponse | undefined,
  originalNarrative: string
): NarrativeOcgValidationHint {
  const results = response?.results ?? []
  const result =
    results.find(candidate => {
      const severity = getHighestSeverity(candidate)
      return (
        candidate.valid === false ||
        Boolean(severity) ||
        (candidate.finding_counts?.total ??
          candidate.narrative_issue_counts?.total ??
          0) > 0
      )
    }) ?? results[0]
  const findings = collectFindings(result)
  const totalCount =
    result?.finding_counts?.total ?? result?.narrative_issue_counts?.total ?? 0
  const replacements = collectReplacements(result)
  const severity = getHighestSeverity(result)
  const visible = Boolean(
    (response?.invalid_count ?? 0) > 0 ||
    (result && (result.valid === false || totalCount > 0 || severity))
  )

  if (!visible) {
    return { visible: false }
  }

  return {
    visible: true,
    source: 'ocg',
    severity,
    keyword: getPrimaryKeyword(result, replacements),
    reason: getPrimaryReason(result, replacements),
    message: findings[0]?.message ?? result?.narrative_rule_hits?.[0]?.message,
    suggestedNarrative: result?.suggested_narrative,
    originalNarrative,
    errorCount:
      result?.finding_counts?.error ??
      result?.narrative_issue_counts?.error ??
      0,
    warningCount:
      result?.finding_counts?.warning ??
      result?.narrative_issue_counts?.warning ??
      0,
    infoCount:
      result?.finding_counts?.info ?? result?.narrative_issue_counts?.info ?? 0,
    totalCount,
    findings,
    ruleHits: collectRuleHits(result),
    replacements,
    instructions:
      Array.isArray(result?.instructions) && result.instructions.length > 0
        ? result.instructions
        : undefined,
    ocgDocument: result?.ocg_document ?? result?.ocg_documents?.[0] ?? null,
    ocgDocuments:
      result?.ocg_documents ??
      (result?.ocg_document ? [result.ocg_document] : []),
  }
}

// MIRA-4349: A context change runs narrative and field rules together. Keep both sets of
// findings and their details instead of letting one response replace the other.
export function normalizeTimesheetValidationResponses(
  responses: TimesheetValidationResponse[],
  originalNarrative: string
): NarrativeOcgValidationHint {
  const hints = responses.map(response =>
    normalizeTimesheetValidationResponse(response, originalNarrative)
  )
  const findings = hints.flatMap(hint => hint.findings ?? [])
  const count = (severity: NarrativeOcgSeverity) =>
    hints.reduce(
      (total, hint) =>
        total +
        (hint[`${severity}Count`] ||
          hint.findings?.filter(finding => finding.severity === severity)
            .length ||
          0),
      0
    )
  const errorCount = count('error')
  const warningCount = count('warning')
  const infoCount = count('info')
  const severity = hints
    .map(hint => hint.severity)
    .filter((value): value is NarrativeOcgSeverity => Boolean(value))
    .sort((a, b) => severityRank[b] - severityRank[a])[0]
  const documents = Array.from(
    new Map(
      hints
        .flatMap(hint => hint.ocgDocuments ?? [])
        .map(document => [JSON.stringify(document), document])
    ).values()
  )
  return {
    ...hints.find(hint => hint.visible),
    visible: hints.some(hint => hint.visible),
    source: 'ocg',
    originalNarrative,
    severity,
    findings,
    errorCount,
    warningCount,
    infoCount,
    totalCount: errorCount + warningCount + infoCount,
    suggestedNarrative: hints.find(hint => hint.suggestedNarrative?.trim())
      ?.suggestedNarrative,
    ruleHits: hints.flatMap(hint => hint.ruleHits ?? []),
    replacements: hints.flatMap(hint => hint.replacements ?? []),
    instructions: hints.flatMap(hint => hint.instructions ?? []),
    ocgDocument: documents[0] ?? null,
    ocgDocuments: documents,
  }
}
